import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, Check, Loader2, RefreshCw, ShieldAlert, Clock, Timer, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const RiscosPage = () => {
  const { api, user } = useAuth();
  const [alertas, setAlertas] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchAlertas();
    fetchEmpregados();
  }, []);

  const fetchAlertas = async () => {
    try {
      const response = await api.get('/alertas-risco?resolvido=false');
      setAlertas(response.data);
    } catch (error) {
      console.error('Error fetching alertas:', error);
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data);
    } catch (error) {
      console.error('Error fetching empregados:', error);
    }
  };

  const verificarRiscos = async () => {
    setVerifying(true);
    try {
      await api.post(`/monitor-risco/verificar?empresa_id=${user?.empresa_id}`);
      toast.success('Verificação concluída!');
      fetchAlertas();
    } catch (error) {
      toast.error('Erro ao verificar riscos');
    } finally {
      setVerifying(false);
    }
  };

  const resolverAlerta = async (id) => {
    try {
      await api.put(`/alertas-risco/${id}/resolver`);
      toast.success('Alerta resolvido!');
      fetchAlertas();
    } catch (error) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const getEmpregadoNome = (id) => {
    const emp = empregados.find(e => e.id === id);
    return emp ? emp.nome : 'Todos';
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      excesso_horas_extras: Timer,
      intrajornada_irregular: Clock,
      interjornada_irregular: Calendar,
      banco_horas_excessivo: Timer,
      inss_threshold: ShieldAlert
    };
    const Icon = icons[tipo] || AlertTriangle;
    return <Icon className="w-5 h-5" />;
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      excesso_horas_extras: 'Excesso de Horas Extras',
      intrajornada_irregular: 'Intrajornada Irregular',
      interjornada_irregular: 'Interjornada < 11h',
      banco_horas_excessivo: 'Banco de Horas Excessivo',
      inss_threshold: 'Alerta INSS (>15 dias)'
    };
    return labels[tipo] || tipo;
  };

  const getSeveridadeBadge = (severidade) => {
    const config = {
      baixo: { label: 'Baixo', className: 'bg-info' },
      medio: { label: 'Médio', className: 'bg-warning' },
      alto: { label: 'Alto', className: 'bg-destructive' }
    };
    const c = config[severidade] || config.medio;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const alertasAlto = alertas.filter(a => a.severidade === 'alto');
  const alertasMedio = alertas.filter(a => a.severidade === 'medio');
  const alertasBaixo = alertas.filter(a => a.severidade === 'baixo');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="riscos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Monitor de Riscos</h2>
          <p className="text-muted-foreground">Alertas de passivo trabalhista</p>
        </div>
        <Button 
          onClick={verificarRiscos}
          disabled={verifying}
          className="bg-accent hover:bg-accent/90"
          data-testid="verificar-riscos-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${verifying ? 'animate-spin' : ''}`} />
          {verifying ? 'Verificando...' : 'Verificar Riscos'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Risco Alto</p>
                <p className="text-3xl font-bold text-destructive">{alertasAlto.length}</p>
              </div>
              <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Risco Médio</p>
                <p className="text-3xl font-bold text-warning">{alertasMedio.length}</p>
              </div>
              <div className="w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-info/50 bg-info/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Risco Baixo</p>
                <p className="text-3xl font-bold text-info">{alertasBaixo.length}</p>
              </div>
              <div className="w-12 h-12 bg-info/10 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Alertas Ativos</CardTitle>
        </CardHeader>
        <CardContent>
          {alertas.length === 0 ? (
            <div className="text-center py-12">
              <Check className="w-12 h-12 mx-auto mb-3 text-accent" />
              <p className="text-lg font-medium text-accent">Nenhum alerta de risco ativo</p>
              <p className="text-sm text-muted-foreground mt-1">
                A empresa está em conformidade com os parâmetros monitorados
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Empregado</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Severidade</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alertas.map((alerta) => (
                    <TableRow key={alerta.id} data-testid={`alerta-row-${alerta.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTipoIcon(alerta.tipo)}
                          <span className="text-sm">{getTipoLabel(alerta.tipo)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{getEmpregadoNome(alerta.empregado_id)}</TableCell>
                      <TableCell className="max-w-xs">{alerta.descricao}</TableCell>
                      <TableCell>{getSeveridadeBadge(alerta.severidade)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(alerta.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => resolverAlerta(alerta.id)}
                          data-testid={`resolver-${alerta.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Resolver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RiscosPage;
